import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, css, Units, useUnitsComponent } from "../../../Styles";
import { NumberDisplayList, Overlay, ProgressCircle, SECTION_ADD, SECTION_BASIC, SECTION_EDIT, SECTION_MOVE} from "../..";
import { SectionBasicTypes } from "../Section/sectionBasic";
import { useAppSelector } from "../../../Store";
import { Graph_QL } from "../../../Services";
import { useAnimal } from "../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/useAnimal";
import { LfLocation } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getLfStructureCall";
import { AnimalInfo } from "../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/getAnimalCall";
interface sectionModal_Props {
  activeSectionId?: number;
  onPenSelected?: (penId: number) => void;
  pens: LfLocation[];
}
/**
 * SectionModal
 * @param activeSectionId The section number.
 * @param onPenSelected Function to handle the penId when a pen is clicked.
 * @returns A "div" to place in a modal to show all content from a Location Feeder Section.
 */
export default function SectionModal({ activeSectionId, onPenSelected, pens }: sectionModal_Props) {
  const { t } = useTranslation();
  const getUnit = useUnitsComponent();

  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => Graph_QL.ObjToString({ domain: farm }), [farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);
  const { data: allAnimalsData } = useAnimal(connection);
  const penIds = pens.map((pen) => pen.location_id);

  const [currentSectionState, setCurrentSectionState] = useState<"ADD" | "MOVE" | "EDIT" | "BASIC">("BASIC");

  function PenClick(penId: number) {
    if (onPenSelected) {
      onPenSelected(penId);
    }
  }

  const animalsInSection = useMemo(() => {
    return allAnimalsData[domain] ? allAnimalsData[domain].data.filter((animal) => penIds.includes(animal.location_id ?? -1)) : []
  }, [allAnimalsData, domain, penIds]);

  // ProgressCircle: Remaining Today
  const RemainingToday: JSX.Element = useMemo(() => {
    const feedTotal = animalsInSection.reduce((accum, animal: AnimalInfo) => accum + (animal.consumptions?.[0]?.total ?? 0) / 1000, 0);
    const feedProgressed = animalsInSection.reduce((accum, animal: AnimalInfo) => accum + (animal.consumptions?.[0]?.progressed ?? 0) / 1000, 0);

    return (
      <ProgressCircle
        animation
        header={t("feed.general.remainingToday", {defaultValue: "remaining today"})}
        total={+(feedTotal).toFixed(2)}
        progressed={+(feedTotal - feedProgressed).toFixed(2)}
        unit={getUnit(Units.kG_grey)}
      />
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animalsInSection, t])

  // ProgressCircle: Farrowing
  const Farrowing: JSX.Element = useMemo(() => {
    const animalsTotal = animalsInSection.length ? animalsInSection.length : 0;
    const animalsFarrowed = animalsInSection.filter((animal: AnimalInfo) => animal.milestone?.milestone_id === 2).length;

    return (
      <ProgressCircle
        animation
        header={t("milestones.defaultMilestones.farrowing", {defaultValue: "Farrowing"})}
        total={animalsTotal}
        progressed={animalsFarrowed}
        unit={getUnit(Units.kG_grey)}
      />
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animalsInSection, t])

  // FeedCorrections
  const FeedCorrections: JSX.Element = useMemo(() => {
    const increasedPens = animalsInSection.filter((animal: AnimalInfo) => animal.correction && animal.correction > 0).length;
    const decreasedPens = animalsInSection.filter((animal: AnimalInfo) => animal.correction && animal.correction < 0).length;

    return (
      <div style={{height: "170px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", marginTop: "18px"}}>
        <NumberDisplayList
          units={[
            t("feed.feedCorrection.increased", { defaultValue: "Increased" }),
            t("feed.feedCorrection.decreased", { defaultValue: "Decreased" }),
            // t("feed.feedCorrection.disabled", { defaultValue: "Disabled" }),
          ]}
          values={[
            increasedPens,
            decreasedPens,
            // disabledPens
          ]}
          header={t("feed.feedCorrection.feedCorrections", {defaultValue: "Feed Corrections"})}
        />
      </div>
    )
  }, [animalsInSection, t])

  // Seperator Line
  const seperatorLine = (
    <div style={{width: "650px", display: "flex", alignItems: "center", justifyContent: "center", margin: "20px auto"}}>
      <div style={{width: "600px", height: "2px", backgroundColor: colors.greyTertiary}}/>
    </div>
  );

  // #region | Pen State Router
  const PenState_Router = () => {
    switch (currentSectionState) {
      case "ADD":
        return (
          <SECTION_ADD
            sectionId={activeSectionId ?? 0}
            pens={pens}
            onCancelBtn={() => setCurrentSectionState("BASIC")}
            onSaveBtn={() => setCurrentSectionState("BASIC")}
          />
        );
      case "MOVE":
        return (
          <SECTION_MOVE
            sectionId={activeSectionId ?? 0}
            pens={pens}
            onCancelBtn={() => setCurrentSectionState("BASIC")}
            onSaveBtn={() => setCurrentSectionState("BASIC")}
          />
        );
      case "EDIT":
        return (
          <SECTION_EDIT
            sectionId={activeSectionId ?? 0}
            pens={pens}
            onCancelBtn={() => setCurrentSectionState("BASIC")}
            onSaveBtn={() => setCurrentSectionState("BASIC")}
          />
        );
      case "BASIC":
        return (
          <SECTION_BASIC
            sectionId={activeSectionId ?? 0}
            pens={pens}
            sectionType={SectionBasicTypes.advancedPen}
            onPenClick={PenClick}
            onAddBtn={() => setCurrentSectionState("ADD")}
            onMoveBtn={() => setCurrentSectionState("MOVE")}
            onEditBtn={() => setCurrentSectionState("EDIT")}
          />
        );
      default:
        return null;
    }
  };
  // #endregion

  return ( <>
    <div style={{ ...css.modalTop }}>
      {RemainingToday}
      {Farrowing}
      {FeedCorrections}
    </div>
    {seperatorLine}
    <div style={{ zIndex: 10000, position: "relative" }}>
      {PenState_Router()}
    </div>
    {currentSectionState !== "BASIC" ? <Overlay overrides={{ color: colors.transparent }} beforeUnload onClickAlert/> : null}
  </>)
}
